// AI-823 useTarotTheater — the thin React hook driving the PURE
// `tarotTheaterReducer`. The reducer owns every transition; this hook owns only
// WHEN to dispatch: the duration timers (min-shuffle, min-jiggle, flying,
// land-hold), the front-image preload gate, the continuous host signals
// (active / streamingDocuments / isStreaming / isInBadQuestionState / hasError),
// and the imperative user-action handlers.
import {
  useReducer, useEffect, useRef, useCallback, useState,
} from 'react'
import type { SubscriptionDocuments } from '../hooks/useOracleChatSubscription'
import {
  tarotTheaterReducer,
  initialTarotTheaterState,
  MIN_SHUFFLE_MS,
  MIN_JIGGLE_MS,
  FLYING_MS,
  LANDED_HOLD_MS,
  TarotPhase,
} from './tarotTheaterMachine'

export interface UseTarotTheaterOptions {
  active: boolean
  streamingDocuments: SubscriptionDocuments | null
  isStreaming?: boolean
  isInBadQuestionState?: boolean
  hasError?: boolean
}

export interface UseTarotTheaterResult {
  phase: TarotPhase
  frontImageUrl: string | undefined
  shuffleElapsedMs: number
  enterNewTarot: () => void
  enterExistingTarot: () => void
  enterExistingNoQuestion: () => void
  chooseCard: () => void
  clarifyingCard: () => void
  submitQuestion: () => void
  cancel: () => void
  flyingDone: () => void
  flipEnd: () => void
  reset: () => void
}

interface TarotDocFields {
  pullId: string
  frontImageUrl: string
}

// Scan the streaming documents for a tarot doc carrying a pullId and a front
// image. Returns the first such doc's fields, or null.
const findTarotDocFields = (
  documents: SubscriptionDocuments | null,
): TarotDocFields | null => {
  if (!documents) return null
  const match = documents
    .map((doc) => (doc as { tarot?: unknown }).tarot as
      | { pullId?: unknown; objects?: Array<{ frontImageUrl?: unknown }> }
      | null
      | undefined)
    .map((tarot) => ({
      pullId: tarot?.pullId,
      frontImageUrl: tarot?.objects?.[0]?.frontImageUrl,
    }))
    .find((fields) => (
      typeof fields.pullId === 'string' && typeof fields.frontImageUrl === 'string'
    ))
  if (match) {
    return {
      pullId: match.pullId as string,
      frontImageUrl: match.frontImageUrl as string,
    }
  }
  return null
}

export const useTarotTheater = (
  options: UseTarotTheaterOptions,
): UseTarotTheaterResult => {
  const {
    active,
    streamingDocuments,
    isStreaming,
    isInBadQuestionState,
    hasError,
  } = options

  const [state, dispatch] = useReducer(
    tarotTheaterReducer,
    initialTarotTheaterState,
  )
  const { phase, frontImageUrl } = state

  const [shuffleElapsedMs, setShuffleElapsedMs] = useState(0)

  // --- imperative handlers ---------------------------------------------------
  const enterNewTarot = useCallback(() => dispatch({ type: 'ENTER_NEW_TAROT' }), [])
  const enterExistingTarot = useCallback(() => dispatch({ type: 'ENTER_EXISTING_TAROT' }), [])
  const enterExistingNoQuestion = useCallback(
    () => dispatch({ type: 'ENTER_EXISTING_TAROT_NO_QUESTION' }),
    [],
  )
  const chooseCard = useCallback(() => dispatch({ type: 'USER_CHOOSES_CARD' }), [])
  const clarifyingCard = useCallback(() => dispatch({ type: 'CLARIFYING_CARD' }), [])
  const submitQuestion = useCallback(() => dispatch({ type: 'QUESTION_SUBMIT' }), [])
  const cancel = useCallback(() => dispatch({ type: 'EXIT_TAROT' }), [])
  const flyingDone = useCallback(() => dispatch({ type: 'FLYING_TIMER' }), [])
  const flipEnd = useCallback(() => dispatch({ type: 'FLIP_TIMER' }), [])
  const reset = useCallback(() => dispatch({ type: 'EXIT_TAROT' }), [])

  // --- continuous signal: active true -> false dispatches EXIT_TAROT ---------
  // EDGE-triggered: we only act on a true->false transition, so prevRef is
  // seeded with the FIRST value (not undefined) — we must NOT treat the initial
  // render as an edge. (Contrast prevStreamingRef below, which is intentionally
  // seeded undefined; the two patterns are deliberately different — do not
  // align them.)
  const prevActiveRef = useRef(active)
  useEffect(() => {
    if (prevActiveRef.current && !active) {
      dispatch({ type: 'EXIT_TAROT' })
    }
    prevActiveRef.current = active
  }, [active])

  // --- continuous signal: tarot doc present -> TAROT_DOC --------------------
  // Dispatch whenever a doc is present and we're in a phase that consumes it:
  // `shuffle` for the normal flow, and the post-shuffle draw phases
  // (`pick_a_card`/`flying`/`jiggle`) for the clarifying draw, which skips
  // shuffle entirely and so must pick up the front image here for the jiggle
  // preload gate. The reducer's consumedPullId dedupe makes re-attempts safe.
  useEffect(() => {
    const docFields = findTarotDocFields(streamingDocuments)
    const consumesDoc = phase === 'shuffle'
      || phase === 'pick_a_card'
      || phase === 'flying'
      || phase === 'jiggle'
    if (docFields && consumesDoc) {
      dispatch({
        type: 'TAROT_DOC',
        pullId: docFields.pullId,
        frontImageUrl: docFields.frontImageUrl,
      })
    }
  }, [streamingDocuments, phase])

  // --- continuous signal: isStreaming -> STREAMING_CHANGED ------------------
  // LEVEL-synced (NOT edge-triggered): prevRef is seeded `undefined` ON PURPOSE
  // so the FIRST observed isStreaming is always pushed into the reducer (whose
  // default is `false`). This is load-bearing: if you "simplify" the seed to
  // `useRef(isStreaming)` like the edge-triggered refs above, an initial
  // isStreaming=true never reaches the reducer, the jiggle latch sees
  // isStreaming=false, and the card flips PREMATURELY (the bug this fixed).
  // Keep the undefined seed and the `!== undefined` guard together.
  const prevStreamingRef = useRef<boolean | undefined>(undefined)
  useEffect(() => {
    if (isStreaming !== undefined && isStreaming !== prevStreamingRef.current) {
      dispatch({ type: 'STREAMING_CHANGED', isStreaming })
    }
    prevStreamingRef.current = isStreaming
  }, [isStreaming])

  // --- edge signal: isInBadQuestionState false -> true -> BACKEND_BAD_QUESTION
  // EDGE-triggered like prevActiveRef: seed with the first value so an initial
  // `true` does not spuriously fire on mount.
  const prevBadQuestionRef = useRef<boolean | undefined>(isInBadQuestionState)
  useEffect(() => {
    if (isInBadQuestionState && !prevBadQuestionRef.current) {
      dispatch({ type: 'BACKEND_BAD_QUESTION' })
    }
    prevBadQuestionRef.current = isInBadQuestionState
  }, [isInBadQuestionState])

  // --- edge signal: tarot stream completed in shuffle without acceptance ----
  // The level-edge `isInBadQuestionState` detector above only fires on a
  // false->true transition, so it CANNOT catch a SECOND consecutive bad
  // question: `isInBadQuestionState` stays `true` the whole time, no edge
  // recurs, and the FSM (moved back to `shuffle` by QUESTION_SUBMIT) would be
  // stuck. This detector complements it with a robust completion-boundary
  // signal: a tarot stream completing (isStreaming true->false) WHILE in
  // `shuffle` and WITHOUT backend acceptance means the question was rejected.
  // A good question always streams a tarot doc (which sets `backendAccepted`
  // via TAROT_DOC) BEFORE the stream ends, so "completed in shuffle with
  // backendAccepted still false" unambiguously means a bad question.
  //
  // EDGE-triggered like prevActiveRef: seed with the FIRST isStreaming value so
  // the initial render is not treated as an edge. NOTE: this is a NEW dedicated
  // ref — do NOT fold it into prevStreamingRef, which is deliberately
  // level-synced (seeded `undefined`) for STREAMING_CHANGED and warns against
  // aligning the patterns.
  const prevStreamingForBadRef = useRef<boolean | undefined>(isStreaming)
  useEffect(() => {
    if (
      prevStreamingForBadRef.current
      && !isStreaming
      && state.phase === 'shuffle'
      && !state.backendAccepted
      && !hasError
    ) {
      dispatch({ type: 'BACKEND_BAD_QUESTION' })
    }
    prevStreamingForBadRef.current = isStreaming
  }, [isStreaming, state.phase, state.backendAccepted, hasError])

  // --- edge signal: hasError false -> true -> BACKEND_ERROR -----------------
  // EDGE-triggered like prevActiveRef: seed with the first value so an initial
  // `true` does not spuriously fire on mount.
  const prevHasErrorRef = useRef<boolean | undefined>(hasError)
  useEffect(() => {
    if (hasError && !prevHasErrorRef.current) {
      dispatch({ type: 'BACKEND_ERROR' })
    }
    prevHasErrorRef.current = hasError
  }, [hasError])

  // --- timer: shuffle -> MIN_SHUFFLE_ELAPSED + elapsed interval -------------
  useEffect(() => {
    if (phase !== 'shuffle') {
      setShuffleElapsedMs(0)
      return undefined
    }
    const startedAt = Date.now()
    setShuffleElapsedMs(0)
    const minTimer = global.setTimeout(() => {
      dispatch({ type: 'MIN_SHUFFLE_ELAPSED', now: Date.now() })
    }, MIN_SHUFFLE_MS)
    const interval = global.setInterval(() => {
      setShuffleElapsedMs(Date.now() - startedAt)
    }, 100)
    return () => {
      global.clearTimeout(minTimer)
      global.clearInterval(interval)
    }
  }, [phase])

  // --- timer: flying -> FLYING_TIMER ----------------------------------------
  useEffect(() => {
    if (phase !== 'flying') return undefined
    const timer = global.setTimeout(() => {
      dispatch({ type: 'FLYING_TIMER' })
    }, FLYING_MS)
    return () => global.clearTimeout(timer)
  }, [phase])

  // --- timer: jiggle -> MIN_JIGGLE_ELAPSED + image preload gate -------------
  useEffect(() => {
    if (phase !== 'jiggle') return undefined
    const minTimer = global.setTimeout(() => {
      dispatch({ type: 'MIN_JIGGLE_ELAPSED' })
    }, MIN_JIGGLE_MS)

    let img: HTMLImageElement | null = null
    if (frontImageUrl) {
      const markReady = () => dispatch({ type: 'FRONT_IMAGE_READY' })
      img = new Image()
      img.onload = markReady
      img.onerror = markReady
      img.src = frontImageUrl
    }

    return () => {
      global.clearTimeout(minTimer)
      if (img) {
        img.onload = null
        img.onerror = null
      }
    }
  }, [phase, frontImageUrl])

  // --- timer: land -> LAND_COMPLETE -----------------------------------------
  useEffect(() => {
    if (phase !== 'land') return undefined
    const timer = global.setTimeout(() => {
      dispatch({ type: 'LAND_COMPLETE' })
    }, LANDED_HOLD_MS)
    return () => global.clearTimeout(timer)
  }, [phase])

  return {
    phase,
    frontImageUrl: frontImageUrl ?? undefined,
    shuffleElapsedMs,
    enterNewTarot,
    enterExistingTarot,
    enterExistingNoQuestion,
    chooseCard,
    clarifyingCard,
    submitQuestion,
    cancel,
    flyingDone,
    flipEnd,
    reset,
  }
}
